import { UserTableService } from './../database/user-table.service';
import { UserInfo } from './../../shared/models/my-info';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, Observable, map, timer, Subscription, switchMap, catchError, of } from 'rxjs';
import { FavouriteLearnerTableService } from '../database/favourite-learner-table.service';
import { Router } from '@angular/router';
import { IUserInfo } from 'src/app/shared/interfaces/user-info';
import { ApiHelper } from 'src/app/core/services/api-helper';

/**
 * True only for an id that can belong to a real staff record. Staff ids start at 1, so 0, a negative,
 * null, undefined or anything that is not a whole number is "no one".
 */
export function isRealStaffId(staffId: unknown): staffId is number {
  return typeof staffId === 'number' && Number.isInteger(staffId) && staffId > 0;
}

/**
 * Whether a /myinfo answer resolved the caller to a staff record. A refusal carries no staff id (`null`,
 * or `0` from an API built before #1873) and must never be cached as the user: screens send the cached
 * id back to the API, and staff 0 has no trainee-access rules, so every filter keyed on it returns all
 * learners.
 */
export function hasResolvedStaffId(myInfo: unknown): boolean {
  return typeof myInfo === 'object' && myInfo !== null && isRealStaffId((myInfo as Record<string, unknown>)['staffid']);
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  public impersonation = false;
  private readonly messageSource: BehaviorSubject<number> = new BehaviorSubject(0);
  public message = this.messageSource.asObservable();

  private readonly apiErrorSource: BehaviorSubject<number> = new BehaviorSubject(0);
  public apiErrorMessage = this.apiErrorSource.asObservable();
  private readonly uiImpersonationSource: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public uiImpersonation: Observable<boolean> = this.uiImpersonationSource.asObservable();

  private readonly userInfoSubject: BehaviorSubject<IUserInfo | null> = new BehaviorSubject<IUserInfo | null>(null);
  public userInfo$ = this.userInfoSubject.asObservable();
  private userInfoSyncSub: Subscription | null = null;
  private userInfoCacheInitialised = false;

  private readonly userInfoSyncInterval = 5 * 60 * 1000;

  apiUrl: string;

  constructor(
    private readonly http: HttpClient,
    private readonly apiHelper: ApiHelper,
    private readonly userTableService: UserTableService,
    private readonly favouriteLearnerTableService: FavouriteLearnerTableService,
    private readonly router: Router
  ) {
    this.apiUrl = environment.API_URL;
  }

  getOptionsWithAccessTokenTwo(token: string): { headers: HttpHeaders } {
    const headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Accept', 'application/json')
      .set('Authorization', token);
    return { headers };
  }

  connectionIssueNotification() {
    this.apiErrorSource.next(1);
  }

  resetInternetConnectionError() {
    this.apiErrorSource.next(0);
  }

  setPresence(profileId: string, status: string, activity: string): Observable<any> {
    const url = `${environment.API_URL}SetUserPresence`;
    const data = { email: profileId, status: status, activity: activity };
    return this.apiHelper.postWithAccessToken(url, data);
  }

  getPresenceInfoMultipleUsers(token: string, userIds: string[] = []): Observable<any> {
    const options = this.getOptionsWithAccessTokenTwo(token);
    const data = { ids: userIds };
    return this.http.post('https://graph.microsoft.com/v1.0/communications/getPresencesByUserId', data, options);
  }

  getMyInfo() {
    const url = `${this.apiUrl}myinfo`;
    return this.apiHelper.getWithAccessToken(url);
  }

  public buildUserFromMyInfoResponse(response: any): IUserInfo {
    return {
      staffId: response['staffid'],
      userName: `${response['forename']} ${response['surname']}`,
      firstName: response['forename'],
      jobRole: response['jobrole'],
      email: response['email'],
      department: response['department'],
      canImpersonate: this.canImpersonateRole(response['jobrole']),
      impersonatingRole: '',
      isImpersonating: response['isImpersonating'] ?? false,
      visionAdmin: response['toolbarSetupStaffconfig'] > 1,
      defaultPage: response['defaultPage'],
      countUnreadNotifications: response['countUnreadNotifications'],
      mode: response['mode'],
      currentLocation: response['currentLocation'],
      microsoftId: response['microsoftId'],
      permission: response['permissions'] ?? [],
      company: response['company'],
    };
  }

  public initializeUserInfoFromCache(): Promise<IUserInfo | null> {
    if (this.userInfoCacheInitialised && this.userInfoSubject.value) {
      return Promise.resolve(this.userInfoSubject.value);
    }

    return this.userTableService.get(1).then((user) => {
      this.userInfoCacheInitialised = true;
      if (user) {
        this.userInfoSubject.next(user);
        if (user.countUnreadNotifications !== undefined) {
          this.messageSource.next(user.countUnreadNotifications);
        }
        return user;
      }
      return null;
    });
  }

  /**
   * Re-read /myinfo every few minutes and keep the cached user current.
   *
   * `onRefused` is required: when the API stops recognising the person mid-session (records changed,
   * account closed, Microsoft account re-linked) the sync stops and hands the refusal over, and the
   * caller signs them out with the reason. A refusal is never cached (#1873). A failed REQUEST is not a
   * refusal: it keeps the session and tries again next time.
   */
  public startUserInfoSync(
    onRefused: (refusal: NonNullable<unknown>) => void,
    intervalMs: number = this.userInfoSyncInterval
  ): void {
    if (this.userInfoSyncSub || this.impersonation) {
      return;
    }

    this.userInfoSyncSub = timer(intervalMs, intervalMs)
      .pipe(
        switchMap(() => {
          if (this.impersonation) {
            return of(null);
          }

          return this.getMyInfo().pipe(
            catchError((error) => {
              console.error('Failed to refresh user info', error);
              this.connectionIssueNotification();
              return of(null);
            })
          );
        })
      )
      .subscribe((response) => {
        if (response === null) {
          return;
        }

        if (!hasResolvedStaffId(response)) {
          this.stopUserInfoSync();
          onRefused(response);
          return;
        }

        this.updateCachedUser(this.buildUserFromMyInfoResponse(response));
      });
  }

  public stopUserInfoSync(): void {
    if (this.userInfoSyncSub) {
      this.userInfoSyncSub.unsubscribe();
      this.userInfoSyncSub = null;
    }
  }

  public refreshUserInfoFromApi(): void {
    if (this.impersonation) {
      return;
    }

    this.getMyInfo()
      .pipe(
        map((response) => this.buildUserFromMyInfoResponse(response)),
        catchError((error) => {
          console.error('Failed to refresh user info', error);
          this.connectionIssueNotification();
          return of(null);
        })
      )
      .subscribe((user) => {
        if (user) {
          this.updateCachedUser(user);
        }
      });
  }

  public patchCachedUser(partialUser: Partial<IUserInfo>): Promise<void> {
    return this.userTableService.get(1).then((existing) => {
      if (!existing) {
        return Promise.resolve();
      }

      const updated: IUserInfo = { ...existing, ...partialUser };
      return this.userTableService.put(updated, 1).then(() => {
        this.userInfoCacheInitialised = true;
        this.userInfoSubject.next(updated);
        if (updated.countUnreadNotifications !== undefined) {
          this.messageSource.next(updated.countUnreadNotifications);
        }
      });
    });
  }

  private updateCachedUser(user: IUserInfo): void {
    // Last line of defence for both refresh paths: never let "no one" replace the signed-in user.
    if (!isRealStaffId(user.staffId)) {
      console.error('Refusing to cache a user with no staff id');
      return;
    }

    this.userTableService.put(user, 1).then(() => {
      this.userInfoCacheInitialised = true;
      this.userInfoSubject.next(user);
      if (user.countUnreadNotifications !== undefined) {
        this.messageSource.next(user.countUnreadNotifications);
      }
    });
  }

  public impersonateUser(user: UserInfo) {
    this.router.navigate(['redirect']).then(() => {
      this.impersonation = true;
      this.userTableService.get(1).then((value) => {
        this.userTableService.clear();
        this.userTableService.add(user, 1);
        this.userTableService.add(value, 2);
        this.router.navigate(['']).then(() => {
          this.uiImpersonationSource.next(true);
          this.userInfoSubject.next(user);
        });
      });
    });
  }

  public cancelImpersonation() {
    this.router.navigate(['redirect']).then(() => {
      this.impersonation = false;
      this.userTableService.get(2).then((value) => {
        this.userTableService.clear();
        this.userTableService.add(value, 1);
        this.router.navigate(['']).then(() => {
          this.uiImpersonationSource.next(true);
          this.userInfoSubject.next(value);
        });
      });
    });
  }

  public saveUserToLocalDbSubscription(user: UserInfo): PromiseLike<void> {
    // Sign-in checks this before calling; refuse here too rather than clear the table and store "no one".
    if (!isRealStaffId(user.staffId)) {
      return Promise.reject(new Error('Refusing to save a user with no staff id'));
    }

    this.userTableService.clear();
    return this.userTableService.add(user, 1).then(() => {
      this.userInfoCacheInitialised = true;
      this.userInfoSubject.next(user);
      if (user.countUnreadNotifications !== undefined) {
        this.messageSource.next(user.countUnreadNotifications);
      }
    });
  }

  public updateNotificationsGlobally(user: UserInfo) {
    this.messageSource.next(user.countUnreadNotifications);
  }

  public updateUserNotifications(user: IUserInfo) {
    this.patchCachedUser({ countUnreadNotifications: user.countUnreadNotifications });
  }

  public resetUserNotifications() {
    this.patchCachedUser({ countUnreadNotifications: 0 });
  }

  public getPermission(staffId: number | string | null) {
    let url = `${environment.API_URL}Permission`;
    if (staffId) {
      url += '/' + staffId;
    }
    return this.apiHelper.getWithAccessToken(url);
  }

  public shareFavouriteReportWithFilter(data: any) {
    const url = `${environment.API_URL}V2/Reporting/ShareReport`;
    return this.apiHelper.postWithAccessToken(url, data);
  }

  public getNotifications(staffId: number) {
    const url = `${environment.API_URL}GetNotifications/${staffId}`;
    return this.apiHelper.getWithAccessToken(url);
  }

  public getFavouriteLearnersFromApi(staffId: number) {
    const url = `${environment.API_URL}GetFavouriteLearners?staffId=${staffId}`;
    return this.apiHelper.getWithAccessToken(url);
  }

  public getFavouriteLearnersFromLocalDb() {
    return this.favouriteLearnerTableService.getAll();
  }

  public addFavouriteLearner(staffId: number, traineeId: string): Observable<any> {
    return this.addFavouriteLearnerRequest(staffId, traineeId).pipe(
      map((response: Observable<any>) => {
        const data = response['data'][0];
        this.favouriteLearnerTableService.add(data, data.id);
        return data.id;
      })
    );
  }

  private addFavouriteLearnerRequest(staffId: number, traineeId: string) {
    const url = `${environment.API_URL}AddFavouriteLearners`;
    const postData = { staffId: staffId, traineeId: traineeId };
    return this.apiHelper.postWithAccessToken(url, postData);
  }

  public removeFavouriteLearner(staffId: number, id: number): Observable<any> {
    return this.removeFavouriteLearnerRequest(staffId, id).pipe(
      map(() => {
        this.favouriteLearnerTableService.remove(id);
      })
    );
  }

  private removeFavouriteLearnerRequest(staffId: number, id: number) {
    const url = `${environment.API_URL}RemoveFavouriteLearners/${staffId}/${id}`;
    return this.apiHelper.deleteWithAccessToken(url);
  }

  public getReportFavouriteStatus(staffId: number, reportId: number): Observable<any> {
    return this.apiHelper.getWithAccessToken(
      `${environment.API_URL}v2/Staff/${staffId}/Favourites/Reports/${reportId}`
    );
  }

  public storeFavouriteReport(data: {
    type: string;
    reportId: number;
    staffId: number;
    storeFilter: { name: string; filter: string } | null;
  }) {
    const url = `${environment.API_URL}v2/Staff/${data.staffId}/Favourites/${data.type}/${data.reportId}`;
    return this.apiHelper.postWithAccessToken(url, data.storeFilter);
  }

  public removeFavouriteReport(data: { type: string; id: number; staffId: number }) {
    const url = `${environment.API_URL}v2/Staff/${data.staffId}/Favourites/${data.type}/${data.id}`;
    return this.apiHelper.deleteWithAccessToken(url);
  }

  private canImpersonateRole(role: string): boolean {
    return role === 'Systems Developer' || role === 'Digital Improvement Manager';
  }
}
